(() => {
  const MIN = 2;
  const MAX = 12;
  const num = (v) => String(Math.round(v * 100) / 100).replace('.', ',');

  const formatQty = (qty, unit) => {
    if (unit === 'g') return qty >= 1000 ? `${num(qty / 1000)} kg` : `${Math.round(qty / 10) * 10} g`;
    if (unit === 'ml') return qty >= 1000 ? `${num(qty / 1000)} l` : `${Math.round(qty / 10) * 10} ml`;
    const rounded = Math.max(0.5, Math.round(qty * 2) / 2);
    return unit ? `${num(rounded)} ${unit}` : num(rounded);
  };

  const packLabel = (v) => {
    if (!(v.grams > 0)) return v.title.replace('.', ',');
    return v.grams >= 1000 ? `${num(v.grams / 1000)} kg` : `${v.grams} g`;
  };

  const packPrice = (packs, currency) => {
    if (!currency || !packs.length || !packs.every((v) => Number.isSafeInteger(v.price) && v.price >= 0)) return '';
    const total = packs.reduce((sum, v) => sum + v.price, 0);
    if (!Number.isSafeInteger(total)) return '';
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(total / 100);
    } catch (e) {
      return '';
    }
  };

  const pickVariant = (variants, need) => {
    const sorted = variants.filter((v) => Number.isFinite(v.grams) && v.grams > 0)
      .sort((a, b) => b.grams - a.grams);
    if (!sorted.length || !Number.isFinite(need) || need <= 0) return [];
    // Minimize excess weight first, then pack count; never exceed eight packs.
    // Keep only one deterministic selection for each partial weight/count.
    let frontier = new Map([[0, []]]);
    let best = null;
    let bestWeight = Infinity;
    for (let count = 1; count <= 8 && frontier.size; count += 1) {
      const next = new Map();
      for (const [weight, packs] of frontier) {
        for (const variant of sorted) {
          const total = weight + variant.grams;
          if (!Number.isFinite(total) || total > bestWeight) continue;
          const selection = [...packs, variant];
          if (total >= need) {
            if (total < bestWeight || (total === bestWeight && selection.length < best.length)) {
              best = selection;
              bestWeight = total;
            }
          } else if (!next.has(total)) {
            next.set(total, selection);
          }
        }
      }
      frontier = next;
    }
    return best || [];
  };

  const init = (root) => {
    if (root.dataset.recipeReady) return;
    root.dataset.recipeReady = 'true';
    const base = Number(root.dataset.recipeBase) || 4;
    const gpp = Number(root.dataset.recipeGpp) || 0;
    const stepper = root.querySelector('[data-recipe-stepper]');
    if (!stepper) return;
    let variants = [];
    const variantsEl = root.querySelector('[data-recipe-variants]');
    const currency = variantsEl?.dataset.currency || '';
    try {
      variants = JSON.parse(variantsEl?.textContent || '[]');
    } catch (e) {
      variants = [];
    }
    let servings = base;

    const render = () => {
      const factor = servings / base;
      root.querySelectorAll('[data-qty]').forEach((el) => {
        el.textContent = formatQty(Number(el.dataset.qty) * factor, el.dataset.unit);
      });
      root.querySelectorAll('[data-recipe-servings-label]').forEach((el) => { el.textContent = `${servings} Personen`; });
      root.querySelectorAll('[data-recipe-servings-inline], [data-recipe-servings-text]').forEach((el) => { el.textContent = servings; });
      stepper.querySelector('[data-recipe-step="-1"]').disabled = servings <= MIN;
      stepper.querySelector('[data-recipe-step="1"]').disabled = servings >= MAX;
      if (!gpp) return;
      const need = servings * gpp;
      root.querySelectorAll('[data-recipe-need]').forEach((el) => { el.textContent = `rund ${need < 1000 ? `${need} g` : `${num(need / 1000)} kg`}`; });
      const packs = pickVariant(variants, need);
      const link = root.querySelector('[data-recipe-pick-link]');
      if (!link) return;
      currentPacks = packs;
      if (!packs.length) {
        link.href = `${link.dataset.productUrl}#r-pdp-buy`;
        const label = link.querySelector('[data-recipe-pick-label]');
        if (label) label.textContent = 'Gewicht am Produkt wählen';
        const text = root.querySelector('[data-recipe-pick-text]');
        if (text) text.textContent = 'Die benötigte Menge wählst du direkt am Produkt.';
        return;
      }
      const first = packLabel(packs[0]);
      const same = packs.every((v) => v.id === packs[0].id);
      const amount = packs.length === 1 ? first : same ? `${packs.length} × ${first}` : packs.map(packLabel).join(' + ');
      link.href = `${link.dataset.productUrl}?variant=${packs[0].id}#r-pdp-buy`;
      const labelEl = link.querySelector('[data-recipe-pick-label]');
      if (labelEl) labelEl.textContent = `${amount} in den Warenkorb`;
      const text = root.querySelector('[data-recipe-pick-text]');
      if (!text) return;
      const price = packPrice(packs, currency);
      const priceSuffix = price ? ` · ${price}` : '';
      text.textContent = packs.length === 1 ? `Passende Packung: ${first}${priceSuffix}` : `Passende Menge: ${amount}${priceSuffix}. Ein Klick legt alle Packungen in den Warenkorb.`;
    };

    // Add every pack in one request; the Kaching cart picks up /cart/add.js and opens its drawer.
    // Without JavaScript or on error the link still opens the product with the first pack selected.
    let currentPacks = [];
    root.querySelector('[data-recipe-pick-link]')?.addEventListener('click', async (event) => {
      const link = event.currentTarget;
      if (!currentPacks.length || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;
      const items = [];
      currentPacks.forEach((v) => {
        const item = items.find((x) => x.id === v.id);
        if (item) item.quantity += 1;
        else items.push({ id: v.id, quantity: 1 });
      });
      link.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error(`cart ${res.status}`);
      } catch (e) {
        window.location.href = link.href;
      } finally {
        link.removeAttribute('aria-busy');
      }
    });

    stepper.hidden = false;
    root.querySelector('[data-recipe-static-note]')?.setAttribute('hidden', '');
    stepper.addEventListener('click', (event) => {
      const button = event.target.closest('[data-recipe-step]');
      if (!button) return;
      servings = Math.min(MAX, Math.max(MIN, servings + Number(button.dataset.recipeStep)));
      render();
    });
    render();
  };

  const boot = () => document.querySelectorAll('[data-recipe]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
